// @flow
import React from 'react';
import { flatten } from 'lodash';
import classNames from 'classnames';
import { Range, getTrackBackground } from 'react-range';
import './styles.scss';

type Props = {
  type?: 'slider' | 'range',
  min: number,
  max: number,
  onChange: (value: number) => void,
  marks?: any,
  step?: number,
  values?: Array<number>,
  showTooltip?: boolean,
  tooltipFormatter?: (value: number) => string,
  disabled?: boolean,
  className?: string,
  sizeFilter?: boolean,
};

const colors = {
  active: '#ef7f7a',
  default: '#f4f4f4',
};

export const Slider = (props: Props) => {
  const {
    type = 'slider',
    min,
    max,
    marks = {
      [props.min]: '',
      [props.max]: '',
    },
    step = 1,
    showTooltip = true,
    tooltipFormatter,
    disabled = false,
    className = '',
    onChange,
    sizeFilter = false,
  } = props;

  const flatData = flatten(props.values);

  let values =
    !!flatData && flatData.length > 0
      ? flatData
      : type === 'range'
      ? [props.min, props.max]
      : [props.min];

  const onSliderChange = (values: Array<number>) => {
    if (type === 'slider') {
      values = values.slice(0, 1);
    } else {
      values = values.slice(0, 2);
    }

    onChange(values);
  };

  const renderThumb = () => {
    if (type === 'slider') {
      return ({ props }) => (
        <div className={'slider-handle'} {...props}>
          {showTooltip && (
            <div className={'slider-tooltip'}>
              {tooltipFormatter(values[0])}
            </div>
          )}
        </div>
      );
    }

    return ({ index, props }) => (
      <div className={'slider-handle'} {...props}>
        {showTooltip && (
          <div className={'slider-tooltip'}>
            {sizeFilter
              ? values[index] === 1
                ? tooltipFormatter('SMALL')
                : values[index] === 2
                ? tooltipFormatter('MEDIUM')
                : values[index] === 3
                ? tooltipFormatter('LARGE')
                : values[index] === 4
                ? tooltipFormatter('VERY LARGE')
                : null
              : tooltipFormatter(values[index])}
          </div>
        )}
      </div>
    );
  };

  const getSliderBackgroundColors = () => {
    if (type === 'slider') {
      return [colors.active, colors.default];
    }

    return [colors.default, colors.active, colors.default];
  };

  const containerClass = classNames('slider-container', className);

  return (
    <div className={containerClass}>
      <Range
        disabled={disabled}
        values={values}
        step={step}
        min={min}
        max={max}
        onChange={values => onSliderChange(values)}
        renderTrack={({ props, children }) => (
          <div
            onMouseDown={props.onMouseDown}
            onTouchStart={props.onTouchStart}
            className={'slider-track-container'}
          >
            <div
              ref={props.ref}
              className={'slider-track'}
              style={{
                background: getTrackBackground({
                  values,
                  min,
                  max,
                  colors: getSliderBackgroundColors(),
                }),
              }}
            >
              {children}
            </div>
          </div>
        )}
        renderThumb={renderThumb()}
      />
    </div>
  );
};
